import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_API_URL } from "../../../utility/api";

export const getPrescriptionDownloadUrl = (id) => {
  const token = localStorage.getItem("token");
  const params = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${BACKEND_API_URL}users/prescriptions/${id}/download${params}`;
};

export const prescriptionApi = createApi({
  reducerPath: "prescriptionApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${BACKEND_API_URL}users/prescriptions`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["Prescriptions", "PrescriptionTemplates"],
  endpoints: (builder) => ({
    saveDraft: builder.mutation({
      query: (body) => ({ url: "/draft", method: "POST", body }),
      invalidatesTags: ["Prescriptions"],
    }),
    finalizePrescription: builder.mutation({
      query: (body) => ({ url: "/finalize", method: "POST", body }),
      invalidatesTags: ["Prescriptions"],
    }),
    getPrescriptions: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        patient_id = "",
        doctor_id = "",
        status = "",
        startDate = "",
        endDate = "",
      } = {}) => {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        });
        if (search) params.append("search", search);
        if (patient_id) params.append("patient_id", patient_id);
        if (doctor_id) params.append("doctor_id", doctor_id);
        if (status) params.append("status", status);
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        return `?${params.toString()}`;
      },
      providesTags: ["Prescriptions"],
    }),
    getPrescriptionById: builder.query({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) => [{ type: "Prescriptions", id }],
    }),
    updatePrescription: builder.mutation({
      query: ({ id, data }) => ({ url: `/${id}`, method: "PUT", body: data }),
      invalidatesTags: ["Prescriptions"],
    }),
    deletePrescription: builder.mutation({
      query: (id) => ({ url: `/${id}`, method: "DELETE" }),
      invalidatesTags: ["Prescriptions"],
    }),
    getPrescriptionTemplates: builder.query({
      query: ({ doctor_id = "", clinic_id = "", search = "" } = {}) => {
        const params = new URLSearchParams();
        if (doctor_id) params.append("doctor_id", doctor_id);
        if (clinic_id) params.append("clinic_id", clinic_id);
        if (search) params.append("search", search);
        return `/templates${params.toString() ? `?${params.toString()}` : ""}`;
      },
      providesTags: ["PrescriptionTemplates"],
    }),
    upsertPrescriptionTemplate: builder.mutation({
      query: (body) => ({ url: "/templates", method: "POST", body }),
      invalidatesTags: ["PrescriptionTemplates"],
    }),
    deletePrescriptionTemplate: builder.mutation({
      query: ({ id, doctor_id = "", clinic_id = "" }) => {
        const params = new URLSearchParams();
        if (doctor_id) params.append("doctor_id", doctor_id);
        if (clinic_id) params.append("clinic_id", clinic_id);
        return {
          url: `/templates/${id}${params.toString() ? `?${params.toString()}` : ""}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["PrescriptionTemplates"],
    }),
  }),
});

export const {
  useSaveDraftMutation,
  useFinalizePrescriptionMutation,
  useGetPrescriptionsQuery,
  useGetPrescriptionByIdQuery,
  useUpdatePrescriptionMutation,
  useDeletePrescriptionMutation,
  useGetPrescriptionTemplatesQuery,
  useUpsertPrescriptionTemplateMutation,
  useDeletePrescriptionTemplateMutation,
} = prescriptionApi;
